import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControlLabel, IconButton, MenuItem, Slider, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import FastForwardIcon from '@mui/icons-material/FastForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { FILE_DELIMITER, fileKeyOf } from './disbox-file-manager';
import { botJson, encodePath, fileUrl, isPublicMode } from './api.js';
import { isVideoFile } from './file-utils';
import {
    findSiblingSubtitles, convertSubtitleToVtt, isAssFileName, fetchMediaInfo, fetchSeekStart,
    embeddedSubtitleUrl, embeddedFontUrl, remuxUrl, subtitleTrackLabel, audioTrackLabel, compatReason,
} from './subtitle-utils';
import { getProgress, saveProgress, formatTime } from './watch-progress';

const UA = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const IS_ANDROID = /Android/i.test(UA);
const IS_IPHONE = /iPhone|iPod/i.test(UA);
const IS_IOS = IS_IPHONE || /iPad/i.test(UA) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const VLC_PACKAGE = 'org.videolan.vlc';
const MX_PACKAGE = 'com.mxtech.videoplayer.ad';

// Renderer ASS asli (JASSUB, libass di WebAssembly). Di iPhone tidak dipakai:
// fullscreen di iPhone hanya untuk elemen <video>, jadi kanvas subtitle
// JASSUB akan hilang saat fullscreen - di sana dipakai VTT (teks polos).
function assRendererSupported() {
    return !IS_IPHONE
        && typeof WebAssembly === 'object'
        && typeof Worker !== 'undefined'
        && typeof HTMLCanvasElement !== 'undefined';
}

// File worker/wasm JASSUB disajikan dari public/jassub/ (disalin otomatis
// saat npm install, lihat scripts/copy-jassub.js).
const JASSUB_BASE = `${window.location.origin}/jassub`;

function waitForJassub(instance, timeoutMs = 45000) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Renderer ASS terlalu lama dimuat.')), timeoutMs);
        instance.addEventListener('ready', () => {
            clearTimeout(timer);
            resolve();
        }, { once: true });
        instance.addEventListener('error', (event) => {
            clearTimeout(timer);
            reject(event.error || new Error('Renderer ASS gagal dimuat.'));
        }, { once: true });
    });
}

function parseVttTime(value) {
    const parts = value.split(':');
    const [secs, ms] = parts.pop().split('.');
    const minutes = Number(parts.pop() || 0);
    const hours = Number(parts.pop() || 0);
    return hours * 3600 + minutes * 60 + Number(secs) + Number(ms || 0) / 1000;
}

function formatVttTime(seconds) {
    const total = Math.max(0, seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = Math.floor(total % 60);
    const ms = Math.round((total - Math.floor(total)) * 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(Math.min(ms, 999)).padStart(3, '0')}`;
}

// Geser semua waktu cue WebVTT (dipakai di mode kompatibel: video mulai dari
// detik ke-X, jadi subtitle harus ikut dimundurkan X detik).
function shiftVtt(vtt, offset) {
    if (!offset) return vtt;
    return vtt.split('\n').map((line) => {
        if (!line.includes('-->')) return line;
        return line.replace(/((?:\d+:)?\d{2}:\d{2}\.\d{3})/g, (match) => formatVttTime(parseVttTime(match) + offset));
    }).join('\n');
}

function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try {
        document.execCommand('copy');
    } finally {
        document.body.removeChild(area);
    }
    return Promise.resolve();
}

function defaultAudioIndex(info) {
    if (!info || !info.audio || info.audio.length === 0) return null;
    return (info.audio.find((a) => a.isDefault) || info.audio[0]).index;
}

// Pilih subtitle tertanam yang paling cocok otomatis (seperti VLC): bahasa
// Indonesia/Melayu dulu, lalu yang bertanda default, lalu yang pertama.
function pickEmbeddedTrack(tracks) {
    const text = (tracks || []).filter((t) => t.text);
    if (text.length === 0) return null;
    const indo = text.find((t) => /^(ind|id|may|ms)$/i.test(t.language || '') || /indo|bahasa/i.test(t.title || ''));
    return indo || text.find((t) => t.isDefault) || text[0];
}

// `playlist` (opsional): urutan episode untuk tombol sebelum/berikutnya. Tanpa
// itu dipakai video lain di folder yang sama (halaman Anime memberi daftar
// lintas folder season).
export default function VideoPlayer({ fileManager, row, onNavigate, onDownload, playlist = null }) {
    const userId = fileManager.userId;
    const path = row.path;
    const node = fileManager.getFile(path) || row;
    const progressKey = fileKeyOf(node) || `p:${path}`;
    const isEncrypted = Boolean(node.enc);

    const videoRef = useRef(null);
    const wrapperRef = useRef(null);
    const subtitleInputRef = useRef(null);
    const pendingSeekRef = useRef(null);
    const lastSaveRef = useRef(0);
    const autoCompatTriedRef = useRef(false);
    const resumeRef = useRef(null);

    const [info, setInfo] = useState(null);
    const [infoError, setInfoError] = useState(null);
    const [compat, setCompat] = useState({ enabled: false, audio: null, start: 0 });
    const [compatLoading, setCompatLoading] = useState(false);
    const [notice, setNotice] = useState(null);
    const [src, setSrc] = useState(() => fileUrl('stream', userId, path));
    const [playbackError, setPlaybackError] = useState(null);
    const [subOptions] = useState(() => findSiblingSubtitles(fileManager, path));
    const [selectedSub, setSelectedSub] = useState('');
    const [sub, setSub] = useState(null);
    const [subLoading, setSubLoading] = useState(false);
    const [subError, setSubError] = useState(null);
    const [useAss, setUseAss] = useState(assRendererSupported());
    const [assActive, setAssActive] = useState(false);
    const [assAttempt, setAssAttempt] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [mediaDuration, setMediaDuration] = useState(null);
    const [resumedFrom, setResumedFrom] = useState(null);
    const [upNext, setUpNext] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [seekDrag, setSeekDrag] = useState(null);
    const [copied, setCopied] = useState(false);

    const absoluteTime = compat.enabled ? compat.start + currentTime : currentTime;
    const duration = (info && info.duration) || mediaDuration || null;
    const absoluteRef = useRef(0);
    absoluteRef.current = absoluteTime;
    const durationRef = useRef(null);
    durationRef.current = duration;

    // ---------- episode sebelum/berikutnya (urutan nama alami) ----------
    const siblings = useMemo(() => {
        if (playlist) return playlist;
        const dirPath = path.substring(0, path.lastIndexOf(FILE_DELIMITER));
        try {
            return Object.values(fileManager.getChildren(dirPath))
                .filter((child) => child.type !== 'directory' && child.content && isVideoFile(child.name))
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
        } catch {
            return [];
        }
    }, [fileManager, path, playlist]);
    const position = siblings.findIndex((s) => s.path === path);
    const prevRow = position > 0 ? siblings[position - 1] : null;
    const nextRow = position >= 0 && position < siblings.length - 1 ? siblings[position + 1] : null;

    // ---------- progres tontonan ----------
    const persistProgress = useCallback((force = false, finished = false) => {
        const position = absoluteRef.current;
        if (!Number.isFinite(position) || (position < 5 && !finished)) return;
        saveProgress(userId, {
            key: progressKey,
            path,
            name: row.name,
            position,
            duration: durationRef.current || 0,
            finished,
        }, { force });
    }, [userId, progressKey, path, row.name]);

    useEffect(() => () => persistProgress(true), [persistProgress]);

    // ---------- info media (track audio/subtitle, chapter) ----------
    useEffect(() => {
        let cancelled = false;
        fetchMediaInfo(userId, path)
            .then((data) => {
                if (cancelled) return;
                setInfo(data);
                const reason = compatReason(data);
                if (reason && !autoCompatTriedRef.current) {
                    autoCompatTriedRef.current = true;
                    setNotice(`${reason}, jadi diputar lewat mode kompatibel (audio diubah ke AAC oleh bot).`);
                    enableCompatRef.current(true, { at: resumeRef.current || absoluteRef.current });
                }
            })
            .catch((error) => {
                if (!cancelled) setInfoError(error.message);
            });
        return () => {
            cancelled = true;
        };
    }, [userId, path]);

    // Subtitle otomatis: file subtitle di folder yang sama dulu, kalau tidak
    // ada baru subtitle tertanam di dalam video.
    const autoSubDoneRef = useRef(false);
    useEffect(() => {
        if (autoSubDoneRef.current) return;
        if (subOptions.length > 0) {
            autoSubDoneRef.current = true;
            loadSubtitleRef.current(subOptions[0].path);
            return;
        }
        if (info) {
            autoSubDoneRef.current = true;
            const track = pickEmbeddedTrack(info.subtitles);
            if (track) loadSubtitleRef.current(`embedded:${track.index}`);
        }
    }, [info, subOptions]);

    // ---------- subtitle ----------
    const loadSubtitle = async (value) => {
        setSubError(null);
        if (value === 'manual-loaded') return;
        if (value === 'manual') {
            if (subtitleInputRef.current) subtitleInputRef.current.click();
            return;
        }
        setSelectedSub(value);
        if (value === '') {
            setSub(null);
            return;
        }
        setSubLoading(true);
        try {
            if (value.startsWith('embedded:')) {
                const index = Number(value.slice('embedded:'.length));
                const track = ((info && info.subtitles) || []).find((t) => t.index === index);
                if (!track) throw new Error('Track subtitle tidak ditemukan.');
                if (!track.text) throw new Error('Subtitle ini berupa gambar (PGS/VobSub), tidak bisa ditampilkan browser. Pakai tombol VLC / MX Player.');
                const isAss = track.codec === 'ass' || track.codec === 'ssa';
                const wantAss = isAss && useAss;
                const response = await fetch(embeddedSubtitleUrl(userId, path, index, wantAss ? 'ass' : 'vtt'));
                if (!response.ok) {
                    const body = await response.json().catch(() => ({}));
                    throw new Error(body.error || `Bot membalas status ${response.status}`);
                }
                const text = await response.text();
                let fonts = [];
                if (wantAss && info.attachments && info.attachments.length > 0) {
                    const data = await botJson(`/subtitles/${userId}/${encodePath(path)}?fonts=1`).catch(() => ({ fonts: [] }));
                    fonts = (data.fonts || []).map((name) => embeddedFontUrl(userId, path, name));
                }
                setSub({ kind: wantAss ? 'ass' : 'vtt', text, fonts, label: subtitleTrackLabel(track) });
            } else {
                const option = subOptions.find((s) => s.path === value);
                if (!option) throw new Error('File subtitle tidak ditemukan.');
                const blob = await fileManager.getPreviewBlob(option.path, 'text/plain');
                const text = await blob.text();
                if (isAssFileName(option.name)) setSub({ kind: 'ass', text, fonts: [], label: option.name });
                else setSub({ kind: 'vtt', text: convertSubtitleToVtt(text, option.name), fonts: [], label: option.name });
            }
        } catch (error) {
            setSubError(error.message || String(error));
            setSelectedSub('');
            setSub(null);
        } finally {
            setSubLoading(false);
        }
    };
    const loadSubtitleRef = useRef(loadSubtitle);
    loadSubtitleRef.current = loadSubtitle;

    const onSubtitleFilePicked = async (event) => {
        const file = event.target.files[0];
        event.target.value = null;
        if (!file) return;
        try {
            const text = await file.text();
            if (isAssFileName(file.name)) setSub({ kind: 'ass', text, fonts: [], label: file.name });
            else setSub({ kind: 'vtt', text: convertSubtitleToVtt(text, file.name), fonts: [], label: file.name });
            setSelectedSub('manual-loaded');
        } catch (error) {
            setSubError(`Gagal memuat file subtitle: ${error.message || error}`);
        }
    };

    // Subtitle ASS tampil sebagai teks biasa selama renderer ASS belum siap
    // (atau gagal), lalu disembunyikan begitu JASSUB aktif.
    const vttUrl = useMemo(() => {
        if (!sub || (sub.kind === 'ass' && useAss && assActive)) return null;
        const vtt = sub.kind === 'ass' ? convertSubtitleToVtt(sub.text, 'subtitle.ass') : sub.text;
        const shifted = compat.enabled ? shiftVtt(vtt, -compat.start) : vtt;
        return URL.createObjectURL(new Blob([shifted], { type: 'text/vtt' }));
    }, [sub, useAss, assActive, compat.enabled, compat.start]);
    useEffect(() => () => {
        if (vttUrl) URL.revokeObjectURL(vttUrl);
    }, [vttUrl]);

    useEffect(() => {
        setAssAttempt(0);
    }, [sub]);

    // Renderer ASS (JASSUB) - dimuat hanya saat dibutuhkan (lazy import).
    useEffect(() => {
        if (!sub || sub.kind !== 'ass' || !useAss || !videoRef.current) {
            setAssActive(false);
            return undefined;
        }
        let instance = null;
        let cancelled = false;
        let retryTimer = null;
        (async () => {
            try {
                const { default: JASSUB } = await import('jassub');
                if (cancelled || !videoRef.current) return;
                instance = new JASSUB({
                    video: videoRef.current,
                    subContent: sub.text,
                    fonts: sub.fonts || [],
                    availableFonts: { 'liberation sans': `${JASSUB_BASE}/default.woff2` },
                    fallbackFont: 'liberation sans',
                    workerUrl: `${JASSUB_BASE}/jassub-worker.js`,
                    wasmUrl: `${JASSUB_BASE}/jassub-worker.wasm`,
                    modernWasmUrl: `${JASSUB_BASE}/jassub-worker-modern.wasm`,
                    timeOffset: compat.enabled ? compat.start : 0,
                });
                await waitForJassub(instance);
                if (!cancelled) setAssActive(true);
            } catch (error) {
                if (cancelled) return;
                setAssActive(false);
                // Gagal sesaat (jaringan sibuk, server web sedang restart): coba
                // lagi 2x sebelum menyerah ke teks biasa.
                if (assAttempt < 2) {
                    console.warn(`[Disbox] Renderer ASS gagal (percobaan ${assAttempt + 1}), dicoba lagi:`, error);
                    retryTimer = setTimeout(() => setAssAttempt((n) => n + 1), assAttempt === 0 ? 3000 : 10000);
                } else {
                    console.warn('[Disbox] Renderer ASS gagal, pakai subtitle teks biasa:', error);
                    setUseAss(false);
                }
            }
        })();
        return () => {
            cancelled = true;
            clearTimeout(retryTimer);
            setAssActive(false);
            if (instance) Promise.resolve(instance.destroy()).catch(() => {});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sub, useAss, compat.enabled, compat.start, src, assAttempt]);

    // ---------- mode kompatibel (remux di bot) ----------
    const enableCompat = async (enable, { audio, at } = {}) => {
        const target = Math.max(0, at !== undefined && at !== null ? at : absoluteRef.current);
        setPlaybackError(null);
        if (!enable) {
            setCompat({ enabled: false, audio: null, start: 0 });
            pendingSeekRef.current = target;
            setSrc(fileUrl('stream', userId, path));
            return;
        }
        setCompatLoading(true);
        try {
            const start = target > 1 ? await fetchSeekStart(userId, path, target) : 0;
            const audioIndex = audio !== undefined ? audio : (compat.audio !== null ? compat.audio : defaultAudioIndex(info));
            setCompat({ enabled: true, audio: audioIndex, start });
            setCurrentTime(0);
            setSrc(remuxUrl(userId, path, { audio: audioIndex, start }));
        } catch (error) {
            setPlaybackError(`Mode kompatibel gagal: ${error.message || error}`);
        } finally {
            setCompatLoading(false);
        }
    };
    const enableCompatRef = useRef(enableCompat);
    enableCompatRef.current = enableCompat;

    const seekTo = (seconds) => {
        const target = Math.max(0, Math.min(seconds, duration || seconds));
        if (compat.enabled) {
            enableCompat(true, { at: target });
        } else if (videoRef.current) {
            videoRef.current.currentTime = target;
        }
    };

    // ---------- event video ----------
    const onLoadedMetadata = () => {
        const video = videoRef.current;
        if (!video) return;
        if (Number.isFinite(video.duration) && !compat.enabled) setMediaDuration(video.duration);
        if (pendingSeekRef.current !== null && !compat.enabled) {
            video.currentTime = pendingSeekRef.current;
            pendingSeekRef.current = null;
            return;
        }
        if (resumeRef.current === null) {
            // Lanjut dari posisi terakhir (sekali saat video pertama dibuka).
            const saved = getProgress(progressKey);
            const total = (info && info.duration) || video.duration;
            if (saved && !saved.finished && saved.position > 20 && (!Number.isFinite(total) || saved.position < total - 30)) {
                resumeRef.current = saved.position;
                setResumedFrom(saved.position);
                if (compat.enabled) enableCompat(true, { at: saved.position });
                else video.currentTime = saved.position;
            } else {
                resumeRef.current = 0;
            }
        }
    };

    const onTimeUpdate = () => {
        const video = videoRef.current;
        if (!video) return;
        setCurrentTime(video.currentTime);
        if (Date.now() - lastSaveRef.current > 5000) {
            lastSaveRef.current = Date.now();
            persistProgress(false);
        }
    };

    const onEnded = () => {
        persistProgress(true, true);
        if (nextRow) setUpNext({ row: nextRow, seconds: 8 });
    };

    const onVideoError = async () => {
        const video = videoRef.current;
        const mediaError = video && video.error;
        console.warn('[Disbox] Video gagal diputar:', mediaError);
        if (!compat.enabled && info && info.video && !autoCompatTriedRef.current) {
            autoCompatTriedRef.current = true;
            setNotice('Browser tidak bisa memutar format ini langsung, dicoba lewat mode kompatibel.');
            enableCompat(true, { at: absoluteRef.current });
            return;
        }
        setPlaybackError(isEncrypted
            ? 'Video terenkripsi ini tidak bisa di-stream (bot tidak punya passphrase enkripsi). Download dulu lalu putar di player lain.'
            : `Video tidak bisa diputar di browser ini. Pakai tombol "Buka di VLC / MX Player"${onDownload ? ' atau download filenya' : ''}.`);
    };

    // Hitung mundur "episode berikutnya".
    const onNavigateRef = useRef(onNavigate);
    onNavigateRef.current = onNavigate;
    useEffect(() => {
        if (!upNext) return undefined;
        if (upNext.seconds <= 0) {
            onNavigateRef.current(upNext.row);
            return undefined;
        }
        const timer = setTimeout(() => setUpNext((current) => (current ? { ...current, seconds: current.seconds - 1 } : null)), 1000);
        return () => clearTimeout(timer);
    }, [upNext]);

    // ---------- fullscreen (dibutuhkan renderer ASS: fullscreen bawaan <video> menutupi kanvas subtitle) ----------
    useEffect(() => {
        const onChange = () => setIsFullscreen(document.fullscreenElement === wrapperRef.current);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const toggleFullscreen = async () => {
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else if (wrapperRef.current && wrapperRef.current.requestFullscreen) {
                await wrapperRef.current.requestFullscreen();
                const orientation = window.screen && window.screen.orientation;
                if (orientation && orientation.lock) orientation.lock('landscape').catch(() => {});
            }
        } catch (error) {
            console.warn('[Disbox] Fullscreen gagal:', error);
        }
    };

    // ---------- chapter: Lewati OP / ED ----------
    const chapters = (info && info.chapters) || [];
    const opChapter = chapters.find((c) => /\b(op|opening|intro|pembuka)\b/i.test(c.title || ''));
    const edChapter = chapters.find((c) => /\b(ed|ending|outro|credits?|penutup)\b/i.test(c.title || ''));
    const inOp = opChapter && absoluteTime >= opChapter.start && absoluteTime < opChapter.end - 1;
    const inEd = edChapter && absoluteTime >= edChapter.start && absoluteTime < edChapter.end - 1;

    // ---------- player luar ----------
    const streamUrl = fileUrl('stream', userId, path);
    const siblingSubPath = selectedSub && !selectedSub.startsWith('embedded:') && selectedSub !== 'manual-loaded' ? selectedSub : null;
    const intentUrl = (pkg) => {
        const url = new URL(streamUrl);
        const extras = [`S.title=${encodeURIComponent(row.label || row.name)}`];
        const ms = Math.floor(absoluteTime * 1000);
        if (ms > 5000) extras.push(pkg === MX_PACKAGE ? `i.position=${ms}` : `l.position=${ms}`);
        if (pkg === VLC_PACKAGE && siblingSubPath) extras.push(`S.subtitles_location=${encodeURIComponent(fileUrl('stream', userId, siblingSubPath))}`);
        if (pkg) extras.push(`S.browser_fallback_url=${encodeURIComponent(`https://play.google.com/store/apps/details?id=${pkg}`)}`);
        return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=${url.protocol.replace(':', '')};type=video/*;${pkg ? `package=${pkg};` : ''}${extras.join(';')};end`;
    };
    const openExternal = (href) => {
        if (videoRef.current) videoRef.current.pause();
        persistProgress(true);
        window.location.href = href;
    };
    const onCopyLink = async () => {
        await copyText(streamUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const audioTracks = (info && info.audio) || [];
    const selectedAudio = compat.enabled ? compat.audio : defaultAudioIndex(info);
    const embeddedTracks = (info && info.subtitles) || [];

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box
                ref={wrapperRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#000',
                    borderRadius: 1,
                    overflow: 'hidden',
                    // Kanvas JASSUB disisipkan SETELAH <video>. Di wadah flex ini ia jatuh
                    // di kanan video (lalu terpotong overflow), dan JASSUB hanya mengoreksi
                    // posisi atas, tidak posisi kiri. Kunci ke pojok kiri atas video.
                    '& > .JASSUB': { position: 'absolute !important', top: 0, left: 0 },
                }}
            >
                <video
                    ref={videoRef}
                    src={src}
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                    controlsList={assActive ? 'nofullscreen' : undefined}
                    // Batas tinggi diatur di sini (bukan lewat CSS :fullscreen): style
                    // inline selalu menang, jadi dulu video tetap 70% layar saat fullscreen.
                    style={{ maxWidth: '100%', maxHeight: isFullscreen ? '100vh' : '70vh', height: isFullscreen ? '100vh' : undefined, width: '100%', objectFit: 'contain', backgroundColor: '#000', display: 'block' }}
                    onLoadedMetadata={onLoadedMetadata}
                    onTimeUpdate={onTimeUpdate}
                    onPause={() => persistProgress(true)}
                    onEnded={onEnded}
                    onError={onVideoError}
                >
                    {vttUrl && <track key={vttUrl} src={vttUrl} kind="subtitles" srcLang="id" label={(sub && sub.label) || 'Subtitle'} default />}
                </video>

                {assActive && (
                    <Tooltip title={isFullscreen ? 'Keluar layar penuh' : 'Layar penuh'}>
                        <IconButton onClick={toggleFullscreen} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}>
                            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Tooltip>
                )}

                {(inOp || inEd) && !upNext && (
                    <Button
                        variant="contained"
                        startIcon={<FastForwardIcon />}
                        onClick={() => {
                            if (inEd && nextRow) onNavigate(nextRow);
                            else seekTo((inOp ? opChapter : edChapter).end);
                        }}
                        sx={{ position: 'absolute', right: 16, bottom: 64, bgcolor: 'rgba(255,255,255,0.9)', color: '#000', '&:hover': { bgcolor: '#fff' } }}
                    >
                        {inOp ? 'Lewati OP' : (nextRow ? 'Episode berikutnya' : 'Lewati ED')}
                    </Button>
                )}

                {upNext && (
                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.75)', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>Episode berikutnya dalam {upNext.seconds} detik</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{upNext.row.label || upNext.row.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="contained" startIcon={<SkipNextIcon />} onClick={() => onNavigate(upNext.row)}>Putar sekarang</Button>
                            <Button variant="outlined" color="inherit" onClick={() => setUpNext(null)}>Batal</Button>
                        </Box>
                    </Box>
                )}

                {compatLoading && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.4)' }}>
                        <CircularProgress />
                    </Box>
                )}
            </Box>

            {compat.enabled && duration && (
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
                    <Typography variant="caption" sx={{ minWidth: 48, textAlign: 'right' }}>{formatTime(seekDrag !== null ? seekDrag : absoluteTime)}</Typography>
                    <Slider
                        size="small"
                        min={0}
                        max={duration}
                        step={1}
                        value={seekDrag !== null ? seekDrag : Math.min(absoluteTime, duration)}
                        onChange={(e, value) => setSeekDrag(value)}
                        onChangeCommitted={(e, value) => {
                            setSeekDrag(null);
                            seekTo(value);
                        }}
                        aria-label="Posisi video"
                    />
                    <Typography variant="caption" sx={{ minWidth: 48 }}>{formatTime(duration)}</Typography>
                </Box>
            )}

            {resumedFrom !== null && resumedFrom > 0 && (
                <Alert severity="info" sx={{ width: '100%' }} action={
                    <Button color="inherit" size="small" onClick={() => { setResumedFrom(null); seekTo(0); }}>Mulai dari awal</Button>
                } onClose={() => setResumedFrom(null)}>
                    Dilanjutkan dari {formatTime(resumedFrom)}
                </Alert>
            )}
            {notice && <Alert severity="info" sx={{ width: '100%' }} onClose={() => setNotice(null)}>{notice}</Alert>}
            {playbackError && <Alert severity="error" sx={{ width: '100%' }}>{playbackError}</Alert>}
            {subError && <Alert severity="warning" sx={{ width: '100%' }} onClose={() => setSubError(null)}>{subError}</Alert>}

            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Tooltip title={prevRow ? `Sebelumnya: ${prevRow.label || prevRow.name}` : 'Tidak ada episode sebelumnya'}>
                    <span>
                        <IconButton disabled={!prevRow} onClick={() => onNavigate(prevRow)}><SkipPreviousIcon /></IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={nextRow ? `Berikutnya: ${nextRow.label || nextRow.name}` : 'Tidak ada episode berikutnya'}>
                    <span>
                        <IconButton disabled={!nextRow} onClick={() => onNavigate(nextRow)}><SkipNextIcon /></IconButton>
                    </span>
                </Tooltip>

                <TextField
                    select
                    size="small"
                    label="Subtitle"
                    value={selectedSub}
                    onChange={(e) => loadSubtitle(e.target.value)}
                    disabled={subLoading}
                    sx={{ minWidth: 200, maxWidth: '100%' }}
                >
                    <MenuItem value="">Tidak ada</MenuItem>
                    {subOptions.map((opt) => (
                        <MenuItem key={opt.path} value={opt.path}>{opt.name}</MenuItem>
                    ))}
                    {embeddedTracks.map((track) => (
                        <MenuItem key={`embedded:${track.index}`} value={`embedded:${track.index}`} disabled={!track.text}>
                            {subtitleTrackLabel(track)}
                        </MenuItem>
                    ))}
                    {selectedSub === 'manual-loaded' && sub && <MenuItem value="manual-loaded">Dari perangkat: {sub.label}</MenuItem>}
                    <MenuItem value="manual">Upload dari perangkat...</MenuItem>
                </TextField>
                <input ref={subtitleInputRef} type="file" accept=".vtt,.srt,.ass,.ssa" style={{ display: 'none' }} onChange={onSubtitleFilePicked} />

                {audioTracks.length > 1 && (
                    <TextField
                        select
                        size="small"
                        label="Audio"
                        value={selectedAudio === null ? '' : selectedAudio}
                        onChange={(e) => enableCompat(true, { audio: Number(e.target.value) })}
                        sx={{ minWidth: 180, maxWidth: '100%' }}
                    >
                        {audioTracks.map((track, i) => (
                            <MenuItem key={track.index} value={track.index}>{audioTrackLabel(track, i)}</MenuItem>
                        ))}
                    </TextField>
                )}

                {info && info.video && (
                    <Tooltip title="Bot menyalin videonya apa adanya dan mengubah audio yang tidak didukung browser (AC3/DTS/dll) ke AAC. Pakai kalau video bisu, salah bahasa, atau tidak mau diputar.">
                        <FormControlLabel
                            control={<Switch checked={compat.enabled} disabled={compatLoading} onChange={(e) => enableCompat(e.target.checked)} />}
                            label="Mode kompatibel"
                        />
                    </Tooltip>
                )}
            </Box>

            {(subLoading || (!info && !infoError)) && (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {subLoading ? 'Menyiapkan subtitle (pertama kali bisa lama untuk video besar)...' : 'Membaca info video...'}
                </Typography>
            )}
            {sub && sub.kind === 'ass' && (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {assActive ? 'Subtitle ASS ditampilkan lengkap (posisi, warna, efek).' : useAss ? 'Menyiapkan tampilan subtitle lengkap...' : 'Subtitle ditampilkan sebagai teks biasa.'}
                    {!useAss && assRendererSupported() && (
                        <Button size="small" sx={{ ml: 1, py: 0, minWidth: 0 }} onClick={() => { setAssAttempt(0); setUseAss(true); }}>Coba tampilan lengkap</Button>
                    )}
                </Typography>
            )}

            <Box sx={{ width: '100%', display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {IS_ANDROID && !isEncrypted && (
                    <>
                        <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={() => openExternal(intentUrl(VLC_PACKAGE))}>Buka di VLC</Button>
                        <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={() => openExternal(intentUrl(MX_PACKAGE))}>MX Player</Button>
                        <Button variant="text" size="small" onClick={() => openExternal(intentUrl(null))}>Player lain...</Button>
                    </>
                )}
                {IS_IOS && !isEncrypted && (
                    <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={() => openExternal(`vlc-x-callback://x-callback-url/stream?url=${encodeURIComponent(streamUrl)}`)}>
                        Buka di VLC
                    </Button>
                )}
                {!IS_ANDROID && !IS_IOS && !isEncrypted && (
                    <Tooltip title={`Di VLC: Media > Open Network Stream (Ctrl+N), lalu tempel link ini.${isPublicMode() ? '' : ' Link berisi token login kamu, jangan dibagikan.'}`}>
                        <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={onCopyLink}>
                            {copied ? 'Link disalin' : 'Salin link untuk VLC'}
                        </Button>
                    </Tooltip>
                )}
                {onDownload && <Button variant="text" size="small" startIcon={<DownloadIcon />} onClick={() => onDownload(row)}>Download</Button>}
                {info && info.video && (
                    <Chip size="small" variant="outlined" label={`${(info.video.codec || '').toUpperCase()}${info.video.height ? ` ${info.video.height}p` : ''}${audioTracks[0] ? ` · ${String(audioTracks[0].codec || '').toUpperCase()}` : ''}`} />
                )}
            </Box>
        </Box>
    );
}
